import React from "react";
import Box from "@mui/material/Box";
import { gr } from "src/design-system/tokens";
import GrButton from "src/design-system/components/GrButton";
import GrSelect from "src/design-system/components/GrSelect";
import { getKmhr, getMihr } from "src/utils/constants";

const distanceOptions = [
  { value: "5km", label: "5 (km)" },
  { value: "10km", label: "10 (km)" },
  { value: "15km", label: "15 (km)" },
  { value: "half_marathon", label: "Half Marathon" },
  { value: "marathon", label: "Marathon" },
];

const inputsGridSx = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: gr.space[4],
  mt: gr.space[4],
  "@media (max-width: 599.95px)": { gridTemplateColumns: "1fr" },
};

// Looks like GrTextField (44px, radius 10, line border, teal focus ring) with an outlined label.
const timeFieldSx = {
  m: 0,
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: gr.space[1],
  height: gr.control.touch,
  px: gr.space[3],
  border: `1px solid ${gr.color.line}`,
  borderRadius: gr.radius.control,
  background: gr.color.surface,
  "&:hover": { borderColor: gr.color.lineStrong },
  "&:focus-within": { borderColor: gr.color.brand, boxShadow: gr.shadow.focus },
};
const timeLegendSx = {
  position: "absolute",
  top: 0,
  left: gr.space[3],
  transform: "translateY(-50%)",
  px: gr.space[1],
  width: "auto",
  float: "none",
  m: 0,
  background: gr.color.surface,
  fontSize: gr.font.size.xs,
  lineHeight: 1,
  color: gr.color.muted,
};
const timeInputSx = {
  width: "3ch",
  border: 0,
  outline: 0,
  p: 0,
  background: "transparent",
  textAlign: "center",
  fontFamily: gr.font.family,
  fontSize: gr.font.size.md,
  color: gr.color.ink,
  MozAppearance: "textfield",
  "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    m: 0,
  },
};

// One panel, one row per zone (hairline between rows): name + value on the left, per-distance select on the right.
const zoneListSx = {
  m: 0,
  p: 0,
  listStyle: "none",
  borderRadius: gr.radius.tile,
  background: gr.color.tint,
  "& > li + li": { borderTop: `1px solid ${gr.color.line}` },
};
const zoneRowSx = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) auto",
  alignItems: "center",
  columnGap: gr.space[3],
  rowGap: "2px",
  px: gr.space[4],
  py: gr.space[3],
  // Phones: the select drops under the value and takes the full width.
  "@media (max-width: 599.95px)": {
    gridTemplateColumns: "1fr",
    rowGap: gr.space[2],
    "& .MuiTextField-root": { width: "100%" },
  },
};
const zoneSelectSx = {
  width: 160,
  minWidth: 0,
  "& .MuiInputBase-input:not(textarea)": {
    py: gr.space[2],
    fontSize: gr.font.size.sm,
  },
};

const PaceCalculator = ({
  checkedPace,
  memberVdotData,
  setMemberVdotDataData,
  setIsShow,
  isShow,
  handleUpdate,
  isFormLoading,
}) => {
  const handleChange = (event) => {
    const { name, value } = event.target;
    setMemberVdotDataData((prevState) => {
      return {
        ...prevState,
        [name]: value,
      };
    });
    setIsShow(true);
  };

  const handleChangeOther = (event) => {
    const { name, value } = event.target;
    setMemberVdotDataData((prevState) => {
      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  const getPaceText = (type) => {
    let result = "";
    if (type?.includes("mi")) {
      result = "Pace Min/Mile ";
    } else if (type?.includes("km")) {
      result = "Pace Min/Km";
    } else {
      result = "Time Cap";
    }
    return result;
  };

  const handleChangeVdotDistance = (name) => {
    let optionsTypeSArray = [];
    switch (name) {
      case "i":
        optionsTypeSArray = [
          { title: "400 Meters Pace", key: "i_400_m" },
          { title: "1 KM Pace", key: "i_km" },
          { title: "1 MILE Pace", key: "i_mi" },
        ];
        break;

      case "t":
        optionsTypeSArray = [
          { title: "1 KM Pace", key: "t_km" },
          { title: "1 MILE Pace", key: "t_mi" },
        ];
        break;

      default:
        optionsTypeSArray = [
          { title: "1 KM Pace", key: "e_km" },
          { title: "1 MILE Pace", key: "e_mi" },
        ];
    }

    if (optionsTypeSArray.length > 0) {
      return optionsTypeSArray;
    }
  };

  const handleHoursChange = (e) => {
    const hours = parseInt(e.target.value) || 0;
    const minutes = Math.floor((memberVdotData.time_interval % 3600) / 60);
    const seconds = memberVdotData.time_interval % 60;
    updateIntervalTime(hours * 3600 + minutes * 60 + seconds);
  };

  const handleMinutesChange = (e) => {
    let minutes = parseInt(e.target.value) || 0;
    minutes = Math.min(Math.max(minutes, 0), 59);
    const currentHours = Math.floor(memberVdotData.time_interval / 3600);
    const seconds = memberVdotData.time_interval % 60;
    updateIntervalTime(currentHours * 3600 + minutes * 60 + seconds);
  };

  const handleSecondsChange = (e) => {
    let seconds = parseInt(e.target.value) || 0;
    seconds = Math.min(Math.max(seconds, 0), 59);
    const minutes = Math.floor(memberVdotData.time_interval / 60);
    updateIntervalTime(minutes * 60 + seconds);
  };

  const updateIntervalTime = (totalSeconds) => {
    setMemberVdotDataData((prevState) => {
      return {
        ...prevState,
        ["time_interval"]: totalSeconds,
      };
    });
    setIsShow(true);
  };

  const formatWithLeadingZeros = (value) => {
    return value < 10 ? `0${value}` : `${value}`;
  };

  let is_allow =
    memberVdotData?.pace ||
    memberVdotData?.paceUp ||
    memberVdotData?.pace1 ||
    memberVdotData?.pace2;

  const mmss = (value) =>
    `${formatWithLeadingZeros(
      Math.floor((value || 0) / 60)
    )}:${formatWithLeadingZeros((value || 0) % 60)}`;

  // Calculated paces (same values and options as before), one row per zone.
  const zones = [
    {
      name: "easy_long",
      label: "Easy / Long",
      options: handleChangeVdotDistance(),
      value: `${mmss(memberVdotData?.pace)} – ${mmss(memberVdotData?.paceUp)}`,
      speed:
        memberVdotData?.pace > 0
          ? `@ Speed ${getKmhr(
              memberVdotData?.pace,
              memberVdotData?.easy_long
            )} km/hr or ${getMihr(
              memberVdotData?.pace,
              memberVdotData?.easy_long
            )} mi/hr - ${getKmhr(
              memberVdotData?.paceUp,
              memberVdotData?.easy_long
            )} km/hr or ${getMihr(
              memberVdotData?.paceUp,
              memberVdotData?.easy_long
            )} mi/hr`
          : null,
    },
    {
      name: "thre_shold",
      label: "Threshold",
      options: handleChangeVdotDistance("t"),
      value: mmss(memberVdotData?.pace1),
      speed:
        memberVdotData?.pace1 > 0
          ? `@ Speed ${getKmhr(
              memberVdotData?.pace1,
              memberVdotData?.thre_shold
            )} km/hr or ${getMihr(
              memberVdotData?.pace1,
              memberVdotData?.thre_shold
            )} mi/hr`
          : null,
    },
    {
      name: "interval",
      label: "Interval",
      options: handleChangeVdotDistance("i"),
      value: mmss(memberVdotData?.pace2),
      speed:
        memberVdotData?.pace2 > 0
          ? `@ Speed ${getKmhr(
              memberVdotData?.pace2,
              memberVdotData?.interval
            )} km/hr or ${getMihr(
              memberVdotData?.pace2,
              memberVdotData?.interval
            )} mi/hr`
          : null,
    },
  ];

  const timeParts = [
    {
      label: "Hours",
      value: formatWithLeadingZeros(
        Math.floor((memberVdotData.time_interval || 0) / 3600)
      ),
      onChange: handleHoursChange,
    },
    {
      label: "Minutes",
      value: formatWithLeadingZeros(
        Math.floor(((memberVdotData.time_interval || 0) % 3600) / 60)
      ),
      onChange: handleMinutesChange,
    },
    {
      label: "Seconds",
      value: formatWithLeadingZeros((memberVdotData.time_interval || 0) % 60),
      onChange: handleSecondsChange,
    },
  ];

  return (
    <Box sx={{ fontFamily: gr.font.family }}>
      {checkedPace && (
        <Box sx={inputsGridSx}>
          <GrSelect
            id="pace-distance"
            label="Distance"
            name="distance"
            value={memberVdotData.distance || ""}
            onChange={handleChange}
            options={distanceOptions}
          />
          {/* Duration hh:mm:ss: three number inputs inside one field. */}
          <Box component="fieldset" sx={timeFieldSx}>
            <Box component="legend" sx={timeLegendSx}>
              Duration (hh:mm:ss)
            </Box>
            {timeParts.map((part, index) => (
              <React.Fragment key={part.label}>
                {index > 0 ? (
                  <Box
                    component="span"
                    aria-hidden
                    sx={{ color: gr.color.muted }}
                  >
                    :
                  </Box>
                ) : null}
                <Box
                  component="input"
                  type="number"
                  aria-label={part.label}
                  value={part.value}
                  onChange={part.onChange}
                  sx={timeInputSx}
                />
              </React.Fragment>
            ))}
          </Box>
        </Box>
      )}
      {isShow && (
        <Box
          sx={{ display: "flex", justifyContent: "flex-end", mt: gr.space[4] }}
        >
          <GrButton
            variant="secondary"
            onClick={(e) => {
              handleUpdate(e);
            }}
            disabled={isFormLoading}
            loading={isFormLoading}
          >
            Update
          </GrButton>
        </Box>
      )}
      {checkedPace && is_allow ? (
        <Box sx={{ mt: gr.space[4] }}>
          <Box
            component="h3"
            sx={{
              m: 0,
              mb: gr.space[2],
              fontSize: gr.font.size.md,
              fontWeight: gr.font.weight.semibold,
              color: gr.color.ink,
            }}
          >
            Your Calculated Pace
          </Box>
          <Box component="ul" sx={zoneListSx}>
            {zones.map((zone) => (
              <Box component="li" key={zone.name} sx={zoneRowSx}>
                <Box sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      fontSize: gr.font.size.sm,
                      fontWeight: gr.font.weight.semibold,
                      color: gr.color.ink2,
                    }}
                  >
                    {zone.label}
                    <Box
                      component="span"
                      sx={{
                        fontWeight: gr.font.weight.regular,
                        color: gr.color.muted,
                      }}
                    >
                      {" · "}
                      {getPaceText(memberVdotData?.[zone.name])}
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: gr.space[2],
                      flexWrap: "wrap",
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: gr.font.size.xl,
                        fontWeight: gr.font.weight.semibold,
                        color: gr.color.brandDark,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {zone.value}
                    </Box>
                  </Box>
                </Box>
                <GrSelect
                  inputProps={{ "aria-label": `${zone.label}: pace per` }}
                  name={zone.name}
                  value={memberVdotData?.[zone.name] || ""}
                  onChange={handleChangeOther}
                  options={(zone.options || []).map((o) => ({
                    value: o.key,
                    label: o.title,
                  }))}
                  fullWidth={false}
                  sx={zoneSelectSx}
                />
                {zone.speed ? (
                  <Box
                    sx={{
                      gridColumn: "1 / -1",
                      fontSize: gr.font.size.xs,
                      color: gr.color.muted,
                    }}
                  >
                    {zone.speed}
                  </Box>
                ) : null}
              </Box>
            ))}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

export default PaceCalculator;
